import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** One-tap star rating shown after a video consult ends. Feeds the provider's reviews. */
export default function ConsultRatingDialog({ isOpen, providerId, providerName, onDone }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("Please tap a star rating first");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reviews/create`, {
        provider_id: providerId,
        user_email: user.email,
        user_name: user.name,
        rating,
        review_text: comment.trim() || "Rated after a virtual consult.",
      });
      toast.success("Thank you! Your rating helps other families.");
      onDone();
    } catch (error) {
      toast.error("Could not save your rating. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onDone()}>
      <DialogContent className="sm:max-w-md" data-testid="consult-rating-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-primary">How was your consult?</DialogTitle>
          <DialogDescription className="font-body">
            Your feedback on {providerName} helps other families choose with confidence.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="flex justify-center gap-2" onMouseLeave={() => setHovered(0)}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                data-testid={`rating-star-${star}`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= (hovered || rating) ? "fill-[#FFB703] text-[#FFB703]" : "text-[#CBD5E1]"
                  }`}
                />
              </button>
            ))}
          </div>
          <Textarea
            data-testid="rating-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Anything you'd like to share? (optional)"
            rows={3}
            className="font-body"
          />
          <div className="flex gap-3">
            <Button
              data-testid="rating-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 min-h-[48px] bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
            >
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Rating"}
            </Button>
            <Button
              data-testid="rating-skip-btn"
              onClick={onDone}
              variant="outline"
              className="min-h-[48px] px-6 border-2 border-[#E2E8F0] rounded-full font-body font-semibold"
            >
              Skip
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
